import React, { useState, useCallback } from 'react';
import AlertModal from '../components/tailwind/common/AlertModal';
import ConfirmationModal from '../components/tailwind/common/ConfirmationModal';

// Note: This hook manages UI state only (no localStorage usage)

export const useCustomDialogs = () => {
  const [alertState, setAlertState] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'warning',
    resolver: null
  });

  const showAlert = useCallback((title, message, type = 'info') => {
    return new Promise((resolve) => {
      setAlertState({
        isOpen: true,
        title,
        message,
        type
      });
      // Wait a moment for the dialog to render, then resolve
      setTimeout(() => resolve(), 100);
    });
  }, []);

  const showConfirm = useCallback((title, message, confirmText = 'Confirm', cancelText = 'Cancel', type = 'warning') => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        title,
        message,
        confirmText,
        cancelText,
        type,
        resolver: resolve
      });
    });
  }, []);

  const handleAlertClose = useCallback(() => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const handleConfirmClose = useCallback((confirmed) => {
    if (confirmState.resolver) {
      confirmState.resolver(confirmed);
    }
    setConfirmState(prev => ({ ...prev, isOpen: false, resolver: null }));
  }, [confirmState.resolver]);

  const AlertDialog = useCallback(() => (
    <AlertModal
      isOpen={alertState.isOpen}
      onClose={handleAlertClose}
      title={alertState.title}
      message={alertState.message}
      type={alertState.type}
    />
  ), [alertState, handleAlertClose]);

  const ConfirmDialog = useCallback(() => (
    <ConfirmationModal
      isOpen={confirmState.isOpen}
      onClose={() => handleConfirmClose(false)}
      onConfirm={() => handleConfirmClose(true)}
      title={confirmState.title}
      message={confirmState.message}
      confirmText={confirmState.confirmText}
      cancelText={confirmState.cancelText}
      type={confirmState.type}
    />
  ), [confirmState, handleConfirmClose]);

  return {
    showAlert,
    showConfirm,
    AlertDialog,
    ConfirmDialog
  };
};